/**
 * AmplitudeProvider (retired)
 *
 * Product analytics is PostHog-only (+ Google Analytics on marketing).
 * This provider stays as a no-op shim so existing `useAmplitude` /
 * `useFeatureFlag` call sites keep compiling without loading Amplitude.
 */

'use client'

import { createContext, ReactNode, useContext, useMemo } from 'react'

type AmplitudeStub = {
  track: (..._args: unknown[]) => void
  identify: (..._args: unknown[]) => void
  setUserId: (..._args: unknown[]) => void
}

type ExperimentStub = {
  variant: (_key: string) => { value: string }
  fetch: () => Promise<void>
}

interface AmplitudeContextType {
  amplitude: AmplitudeStub
  experiment: ExperimentStub
  isReady: boolean
}

const stubAmplitude: AmplitudeStub = {
  track: () => undefined,
  identify: () => undefined,
  setUserId: () => undefined
}

const stubExperiment: ExperimentStub = {
  variant: () => ({ value: 'off' }),
  fetch: async () => undefined
}

const AmplitudeContext = createContext<AmplitudeContextType | undefined>(undefined)

export function AmplitudeProvider({
  children,
  disabled: _disabled = false
}: {
  children: ReactNode
  /** Ignored — Amplitude analytics is retired. */
  disabled?: boolean
}) {
  const value = useMemo<AmplitudeContextType>(
    () => ({
      amplitude: stubAmplitude,
      experiment: stubExperiment,
      isReady: true
    }),
    []
  )

  return <AmplitudeContext.Provider value={value}>{children}</AmplitudeContext.Provider>
}

export function useAmplitude() {
  const context = useContext(AmplitudeContext)
  if (context === undefined) {
    throw new Error('useAmplitude must be used within an AmplitudeProvider')
  }
  return context
}
